/**
 * The floating Nexus Dex orb: the running Live Scan, in-app (2026-09-28).
 *
 * WHY. Trainers hop between GO and Nexus Dex mid-scan. The Live Scan sheet
 * they started from stayed open over the Pokédex / My Pokémon every time
 * they came back (and the Pokédex re-opened it on mount), so they closed it
 * every time. Now the sheet dismisses itself when the scan goes live
 * (DexLiveScanMenu) and this orb stands in for it: a small medallion,
 * pulsing while the scan runs, that re-opens the same sheet (with the live
 * status and the Stop & save control) on a tap.
 *
 * WHERE. Fixed, bottom-right by default, one row above the Pokédex
 * next-region and My Pokémon back-to-top buttons (both sit at +84px), clear
 * of the tab bar and the safe areas. DRAGGABLE (owner ask, same day): a
 * press-and-move puts it anywhere on screen, the spot is remembered per
 * device (localStorage, a per-viewer convenience) and clamped back inside
 * the viewport on every show, so a rotation or a smaller screen can never
 * lose it. A tap (no real movement) opens the sheet. Mounted once in
 * App.tsx, route-independent, so it survives navigation. Hidden while the
 * sheet itself is open (the sheet is z-[75]).
 *
 * NOT hover-elevate: that utility sets `position: relative`, which beat
 * `fixed` on the first device pass and put the orb in normal flow at the top
 * of the app, 56px tall, pushing every page's header down.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDexLiveScanMenuOpen, openDexLiveScanMenu } from "@/lib/dexLiveScanMenuState";
import { useLiveScanPhase } from "@/components/LiveScanProvider";
import { hapticTick } from "@/lib/haptics";
import { getPlatform } from "@/lib/platform";
import nexusDexMedallion from "@/assets/nexus_dex_medallion_3d.png";

export const LIVE_SCAN_ORB_SIZE_PX = 56;
/** Finger travel under this is a tap, not a drag. */
export const LIVE_SCAN_ORB_TAP_SLOP_PX = 8;
export const LIVE_SCAN_ORB_POSITION_KEY = "nexus.liveScanOrb.pos.v1";
/** The orb never sits closer than this to a viewport edge. */
const EDGE_MARGIN_PX = 8;

export interface OrbPosition {
  /** Left / top of the orb in CSS px of the viewport. */
  x: number;
  y: number;
}

export function readOrbPosition(): OrbPosition | null {
  try {
    const raw = window.localStorage.getItem(LIVE_SCAN_ORB_POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrbPosition>;
    if (typeof parsed.x !== "number" || typeof parsed.y !== "number") return null;
    if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return null;
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

export function writeOrbPosition(pos: OrbPosition | null): void {
  try {
    if (pos) window.localStorage.setItem(LIVE_SCAN_ORB_POSITION_KEY, JSON.stringify(pos));
    else window.localStorage.removeItem(LIVE_SCAN_ORB_POSITION_KEY);
  } catch {
    /* storage unavailable: the orb simply forgets */
  }
}

/** Keep the whole orb on screen, clear of the edges. */
export function clampOrbPosition(pos: OrbPosition, viewportW: number, viewportH: number): OrbPosition {
  const maxX = Math.max(EDGE_MARGIN_PX, viewportW - LIVE_SCAN_ORB_SIZE_PX - EDGE_MARGIN_PX);
  const maxY = Math.max(EDGE_MARGIN_PX, viewportH - LIVE_SCAN_ORB_SIZE_PX - EDGE_MARGIN_PX);
  return {
    x: Math.min(maxX, Math.max(EDGE_MARGIN_PX, Math.round(pos.x))),
    y: Math.min(maxY, Math.max(EDGE_MARGIN_PX, Math.round(pos.y))),
  };
}

export function LiveScanFloatingOrb() {
  const phase = useLiveScanPhase();
  const menuOpen = useDexLiveScanMenuOpen();
  const running = phase === "starting" || phase === "scanning" || phase === "finishing";

  // The remembered spot, clamped to this viewport; null = the default corner.
  const [pos, setPos] = useState<OrbPosition | null>(() => {
    const saved = typeof window === "undefined" ? null : readOrbPosition();
    return saved ? clampOrbPosition(saved, window.innerWidth, window.innerHeight) : null;
  });
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // A rotation or a resize must never leave the orb off screen.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => (p ? clampOrbPosition(p, window.innerWidth, window.innerHeight) : p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    };
    // Keep receiving moves when the finger leaves the button (jsdom lacks it).
    el.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < LIVE_SCAN_ORB_TAP_SLOP_PX) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(true);
    }
    setPos(clampOrbPosition({ x: d.originX + dx, y: d.originY + dy }, window.innerWidth, window.innerHeight));
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<HTMLButtonElement>, cancelled: boolean) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    buttonRef.current?.releasePointerCapture?.(e.pointerId);
    if (d.moved) {
      setDragging(false);
      // The spot lands where the finger let go; remember it.
      setPos((p) => {
        if (p) writeOrbPosition(p);
        return p;
      });
      return;
    }
    if (cancelled) return;
    // A tap: open the sheet.
    void hapticTick();
    openDexLiveScanMenu();
  }, []);

  // ANDROID: the native HUD bubble floats over Nexus Dex too, and a tap on
  // it while the app is in front opens this same sheet (2026-09-29 owner
  // ask: two look-alike roundels). One roundel, so this one stands down.
  if (!running || menuOpen || getPlatform() === "android") return null;

  const placement: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y }
    : { right: "max(12px, var(--nx-safe-right))", bottom: "calc(var(--nx-safe-bottom) + 140px)" };

  return (
    <button
      ref={buttonRef}
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => endDrag(e, false)}
      onPointerCancel={(e) => endDrag(e, true)}
      // The tap already opened the sheet on pointer-up; a synthetic click
      // after a drag must not open it too.
      onClick={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      aria-label="Live Scan is running. Tap to open the Live Scan menu, drag to move"
      data-testid="live-scan-floating-orb"
      data-phase={phase}
      data-dragging={dragging ? "true" : "false"}
      data-placed={pos ? "custom" : "default"}
      className="fixed z-[60] rounded-full grid place-items-center select-none touch-none"
      style={{
        ...placement,
        width: LIVE_SCAN_ORB_SIZE_PX,
        height: LIVE_SCAN_ORB_SIZE_PX,
        background: "var(--surface-overlay)",
        border: "2px solid var(--accent-fill)",
        boxShadow: dragging ? "0 10px 28px var(--accent-fill-glow)" : "0 6px 20px var(--accent-fill-glow)",
        transform: dragging ? "scale(1.08)" : "scale(1)",
        transition: dragging ? "none" : "transform var(--duration-fast), box-shadow var(--duration-fast)",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
      }}
    >
      {/* The pulse says "live" the way the scan HUD's dot does. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full animate-ping"
        style={{ border: "2px solid var(--accent-fill)", opacity: 0.45 }}
      />
      <img
        src={nexusDexMedallion}
        alt=""
        draggable={false}
        className="relative rounded-full select-none pointer-events-none"
        style={{ width: LIVE_SCAN_ORB_SIZE_PX - 8, height: LIVE_SCAN_ORB_SIZE_PX - 8 }}
      />
      <span
        aria-hidden
        data-testid="live-scan-floating-orb-dot"
        className="absolute rounded-full"
        style={{
          top: 2,
          right: 2,
          width: 12,
          height: 12,
          background: "hsl(var(--destructive))",
          border: "2px solid var(--surface-overlay)",
        }}
      />
    </button>
  );
}
